import { EMPTY_BITRATE_CHART, type BitrateChartData } from "../utils/bitrateHistory";
import { useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Masonry from "@mui/lab/Masonry";
import { type StreamStateValue } from "./StreamState";
import StreamCard from "./StreamCard";
import StreamCardBrief from "./StreamCardBrief";
import StreamPreviewPopover, { type StreamPreviewPopoverOpenAnimation } from "./StreamPreviewPopover";
import { useAppContext } from "../context/AppContext";
import { useStreamPreviewCollection } from "../hooks/useStreamPreviewCollection";
import type { StreamSummary } from "../types/streamSummary";
import { type StreamOutputStatsSnapshot } from "../utils/streamOutputs";
import type { FileSourceInfoSnapshot } from "../types/fileSourceInfo";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundImage: "none",
  boxShadow: "none",
  "&:before": {
    display: "none"
  },
  "&.Mui-expanded": {
    margin: 0
  }
}));

const MIN_STREAM_COLUMN_WIDTH = 260;

type StreamMasonryProps = {
  streams: StreamSummary[];
  canManageStreams: boolean;
  canViewDetectionInfo: boolean;
  commandsInFlight: Record<string, "start" | "stop">;
  streamBitratesById: Map<string, BitrateChartData>;
  fileSourceInfoByStream: Map<string, FileSourceInfoSnapshot>;
  onCommand: (args: { streamId: string; command: "start" | "stop" }) => void;
  onOpenEdit: (stream: StreamSummary) => void;
  onOpenDelete: (stream: StreamSummary) => void;
  onOpenDetection: (stream: StreamSummary) => void;
  onToggleTimeline: (stream: StreamSummary) => void;
  onToggleCalendar: (stream: StreamSummary) => void;
  onOpenPlayer: (stream: StreamSummary) => void;
  timelineStreamId: string | null;
  calendarStreamId: string | null;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  getStreamStateMessage: (streamId: string) => unknown;
  getOutputState: (streamId: string, outputId: string) => StreamStateValue | undefined;
  getOutputStateMessage: (streamId: string, outputId: string) => string | null;
  outputStatsByStream: Map<string, Map<string, StreamOutputStatsSnapshot>>;
  previewOpenAnimation?: StreamPreviewPopoverOpenAnimation;
};

const StreamMasonry = ({
  streams,
  canManageStreams,
  canViewDetectionInfo,
  commandsInFlight,
  streamBitratesById,
  fileSourceInfoByStream,
  onCommand,
  onOpenEdit,
  onOpenDelete,
  onOpenDetection,
  onToggleTimeline,
  onToggleCalendar,
  onOpenPlayer,
  timelineStreamId,
  calendarStreamId,
  getStreamState,
  getStreamStateMessage,
  getOutputState,
  getOutputStateMessage,
  outputStatsByStream,
  previewOpenAnimation = "none"
}: StreamMasonryProps) => {
  const { serverConfig } = useAppContext();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [expandedStreamIds, setExpandedStreamIds] = useState<Set<string>>(new Set());
  const expandedIds = useMemo(
    () => streams.filter((stream) => expandedStreamIds.has(stream.id)).map((stream) => stream.id),
    [expandedStreamIds, streams]
  );
  const { previewUrls } = useStreamPreviewCollection({
    streamIds: expandedIds,
    getStreamState,
    getStreamStateMessage
  });

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width } = entry.contentRect;
      setContainerWidth((current) => (current === width ? current : width));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (!streams.length) {
    return null;
  }

  const columns = Math.max(1, Math.floor(containerWidth / MIN_STREAM_COLUMN_WIDTH));

  return (
    <Box ref={containerRef}>
      <Masonry columns={columns} spacing={1}>
        {streams.map((stream) => {
          const pendingCommand = commandsInFlight[stream.id];
          const isTimelineSelected = timelineStreamId === stream.id;
          const isCalendarSelected = calendarStreamId === stream.id;
          const streamOutputStats = outputStatsByStream.get(stream.id);
          const fileSourceInfo = fileSourceInfoByStream.get(stream.id);
          const isExpanded = expandedStreamIds.has(stream.id);
          const streamState = getStreamState(stream.id);
          const title = stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor;

          return (
            <StyledAccordion
              key={stream.id}
              disableGutters
              slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
              expanded={isExpanded}
              onChange={(_, nextExpanded) => {
                setExpandedStreamIds((current) => {
                  const next = new Set(current);
                  if (nextExpanded) {
                    next.add(stream.id);
                  } else {
                    next.delete(stream.id);
                  }
                  return next;
                });
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1,
                  py: 0.5,
                  "& .MuiAccordionSummary-content": {
                    margin: 0
                  }
                }}
              >
                {isExpanded ? (
                  <StreamCardBrief
                    stream={stream}
                    fileSourceInfo={fileSourceInfo}
                    streamOutputStats={streamOutputStats}
                    getStreamState={getStreamState}
                    getStreamStateMessage={getStreamStateMessage}
                    getOutputState={getOutputState}
                    getOutputStateMessage={getOutputStateMessage}
                    hideOutputs={isExpanded}
                  />
                ) : (
                  <StreamPreviewPopover
                    streamId={stream.id}
                    title={title}
                    streamState={streamState}
                    disabled={!serverConfig.enablePreview}
                    openAnimation={previewOpenAnimation}
                  >
                    <Box sx={{ width: "100%" }}>
                      <StreamCardBrief
                        stream={stream}
                        fileSourceInfo={fileSourceInfo}
                        streamOutputStats={streamOutputStats}
                        getStreamState={getStreamState}
                        getStreamStateMessage={getStreamStateMessage}
                        getOutputState={getOutputState}
                        getOutputStateMessage={getOutputStateMessage}
                        hideOutputs={isExpanded}
                      />
                    </Box>
                  </StreamPreviewPopover>
                )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1, pt: 0, pb: 1.5 }}>
              {isExpanded ? (
                <StreamCard
                  stream={stream}
                  canManageStreams={canManageStreams}
                  canViewDetectionInfo={canViewDetectionInfo}
                  pendingCommand={pendingCommand}
                  isTimelineSelected={isTimelineSelected}
                  isCalendarSelected={isCalendarSelected}
                  bitrates={streamBitratesById.get(stream.id) ?? EMPTY_BITRATE_CHART}
                  previewUrl={previewUrls[stream.id]}
                  fileSourceInfo={fileSourceInfo}
                  streamOutputStats={streamOutputStats}
                  onCommand={onCommand}
                  onOpenEdit={onOpenEdit}
                  onOpenDelete={onOpenDelete}
                  onOpenDetection={onOpenDetection}
                  onToggleTimeline={onToggleTimeline}
                  onToggleCalendar={onToggleCalendar}
                  onOpenPlayer={onOpenPlayer}
                  getStreamState={getStreamState}
                  getStreamStateMessage={getStreamStateMessage}
                  getOutputState={getOutputState}
                  getOutputStateMessage={getOutputStateMessage}
                  hideStreamName
                  previewOpenAnimation={previewOpenAnimation}
                />
              ) : null}
            </AccordionDetails>
          </StyledAccordion>
        );
        })}
      </Masonry>
    </Box>
  );
};

export default StreamMasonry;
